"use client";

import { Copy, Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { Prompt } from "@/lib/data";
import Link from "next/link";
import { slugify } from "@/lib/utils";
import { useState, useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { CopyPromptButton } from "@/components/copy-prompt-button";

// Helper function for initial truncation (works on server and client)
const truncateText = (text: string, maxLength: number = 150): string => {
  if (!text) return '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

export function PromptCard({ prompt }: { prompt: Prompt }) {
  // Do basic truncation for server rendering to avoid hydration mismatch
  const initialContent = truncateText(prompt.content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim());
  const [truncatedContent, setTruncatedContent] = useState(initialContent);
  const plainTextRef = useRef<string>("");

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Clean the HTML content
      const sanitizedContent = DOMPurify.sanitize(prompt.content);
      
      // Create a temporary div to extract text content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = sanitizedContent;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      
      // Truncate the content
      setTruncatedContent(truncateText(textContent));
      
      // Store plain text version for copying
      plainTextRef.current = textContent;
    }
  }, [prompt.content]);

  const promptSlug = slugify(prompt.title);
  const categorySlug = slugify(prompt.category);
  const { is_advanced } = prompt;

  return (
    <Card className="h-full flex flex-col border-[#A4AFB7] hover:border-zinc-900 transition-colors rounded-[15px]">
      <CardHeader className="relative space-y-3">
        <div className="flex justify-between items-start">
          {prompt.category && (
            <Link href={`/chatgpt-prompts/category/${categorySlug}`}>
              <Badge 
                variant="outline" 
                className="w-fit text-[14px] text-[#536066] px-[15px] py-[5px] border border-[#A4AFB7] rounded-[15px] bg-white hover:bg-[#B8EF52]/10 hover:border-[#B8EF52] transition-colors"
              >
                {prompt.category}
              </Badge>
            </Link>
          )}
          
          {is_advanced && (
            <Badge
              className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white px-[12px] py-[4px] rounded-[15px] pointer-events-none flex items-center gap-1"
            >
              <Brain className="h-3.5 w-3.5" />
              Advanced
            </Badge>
          )}
        </div>
        <Link href={`/chatgpt-prompts/${promptSlug}`} className="transition-colors">
          <h3 className="text-lg font-medium">{prompt.title}</h3>
        </Link>
      </CardHeader>
      <CardContent className="flex-grow">
        <p className="text-[15px] text-muted-foreground">{truncatedContent}</p>
      </CardContent>
      <CardFooter className="flex justify-between items-center gap-2">
        <Link href={`/chatgpt-prompts/${promptSlug}`} className="flex-1">
          <Button 
            className="w-full rounded-[50px] bg-[#B8EF52] text-zinc-900 hover:bg-[#a5d649] px-[30px] py-[20px]"
            size="sm"
          >
            View Prompt
          </Button>
        </Link>
        <CopyPromptButton 
          content={plainTextRef.current || prompt.content} 
          promptId={prompt.id} 
          label="Copy" 
          variant="outline"
        />
      </CardFooter>
    </Card>
  );
}